import { Container, IDestroyOptions, Sprite } from "pixi.js";
import { MyWalletResourcePackage } from "../../../../resources/MyWalletResourcePackage";
import { VBox } from "../../../../../../common/components/ui/VBox";
import { TokenType } from "../../../../../../../../web3/TokenType";
import { Redirector } from "../../../../../../../../tools/Redirector";
import { ethereum } from "thirdweb/chains";
import { nameof } from "../../../../../../../../shared/bindable-data";
import { Web3Model } from "../../../../../../../../web3/models/Web3Model";
import { Web3Config } from "../../../../../../../../web3/config/Web3Config";
import { ILogger } from "../../../../../../../logger/ILogger";
import { Event } from "../../../../../../../../shared/event-system/Event";
import { BaseTokenPanel } from "../panels/BaseTokenPanel";
import { MONPanel } from "../panels/MONPanel";
import { USDCPanel } from "../panels/USDCPanel";
import { ITokenFrame } from "./ITokenFrame";
import gsap from "gsap";
import { StringFormatExtention } from "../../../../../../../../extensions/StringFormatExtention";

export class MainTokenFrame extends Container implements ITokenFrame {
    private readonly backgrond: Sprite;
    private readonly tokensBox: VBox;
    private readonly tokenPanels: Map<TokenType, BaseTokenPanel>;
    private readonly showTimeline: gsap.core.Timeline;

    public get onSendMonClick(): Event {
        const monPanel = this.tokenPanels.get(TokenType.MON) as MONPanel;
        return monPanel.onSendClick;
    }

    public get onReceiveMonClick(): Event {
        const monPanel = this.tokenPanels.get(TokenType.MON) as MONPanel;
        return monPanel.onReceiveClick;
    }

    public get onSendUsdcClick(): Event {
        const usdcPanel = this.tokenPanels.get(TokenType.USDC) as USDCPanel;
        return usdcPanel.onSendClick;
    }

    public get onReceiveUsdcClick(): Event {
        const usdcPanel = this.tokenPanels.get(TokenType.USDC) as USDCPanel;
        return usdcPanel.onReceiveClick;
    }

    public constructor(
        private readonly myWalletResources: MyWalletResourcePackage,
        private readonly web3Model: Web3Model,
        private readonly web3Config: Web3Config,
        private readonly logger: ILogger
    ) {
        super();

        this.backgrond = new Sprite(this.myWalletResources.assets_views_my_wallet_ui_tokens_frame_png);
        this.backgrond.position.set(-67, 41);

        this.tokensBox = new VBox(20);
        this.tokensBox.position.set(-59, 80);
        this.tokenPanels = new Map();
        const tokens = Object.keys(TokenType);

        for (let i = 0; i < tokens.length; i++) {
            if (i > 0) {
                this.tokensBox.addChild(new Sprite(this.myWalletResources.assets_views_my_wallet_ui_tokens_separator_png));
            }
            const tokenType = tokens[i] as TokenType;
            const tokenPanel = this.createPanel(tokenType);
            this.tokensBox.addChild(tokenPanel);
            this.tokenPanels.set(tokenType, tokenPanel);
        }

        this.addChild(
            this.backgrond,
            this.tokensBox
        );

        this.showTimeline = new gsap.core.Timeline({ paused: true });
        this.web3Model.propertyChanged.add(this.onWeb3ModelPropertyChanged, this);
    }

    public async show(): Promise<void> {
        this.showTimeline.clear();
        this.showTimeline.fromTo(this.backgrond, { alpha: 0 }, { alpha: 1, duration: 0.15 }, "<");
        this.showTimeline.fromTo(this.tokensBox, { alpha: 0 }, { alpha: 1, duration: 0.15 }, "<");
        for (const panel of this.tokenPanels.values()) {
            this.showTimeline.fromTo(panel, { alpha: 0 }, { onStart: () => panel.layout(), alpha: 1, duration: 0.3 }, "<60%");
        }
        await this.showTimeline.play();
    }

    public async hide(): Promise<void> {
        await this.showTimeline.reverse();
    }

    public override destroy(options?: boolean | IDestroyOptions): void {
        this.showTimeline.kill();
        this.web3Model.propertyChanged.remove(this.onWeb3ModelPropertyChanged, this);
        for (const panel of this.tokenPanels.values()) {
            panel.onSeeTransactionsClick.remove(this.onSeeTransactionsClick, this);
        }
        super.destroy(options);
    }

    private createPanel(tokenType: TokenType): BaseTokenPanel {
        let panel = null;
        switch (tokenType) {
            case TokenType.USDC:
                panel = new USDCPanel(this.myWalletResources, this.formatBalance(this.web3Model.balanceUSDC.value));
                break;
            case TokenType.MON:
                panel = new MONPanel(this.myWalletResources, this.formatMonBalance(this.web3Model.balanceMON.value));
                break;
            default:
                throw new Error(`Unable to create unsupported token ${tokenType}`);
        }
        panel.onSeeTransactionsClick.add(this.onSeeTransactionsClick, this);
        return panel;
    }

    private formatBalance(value: string): string {
        const numValue = Number(value);
        return isNaN(numValue) || numValue === 0 ? "0.0000" : StringFormatExtention.tokensFormat(value);
    }

    private formatMonBalance(value: string): string {
        const numValue = Number(value);
        return isNaN(numValue) || numValue === 0 ? "0.0000" : StringFormatExtention.tokensFormat(value);
    }

    private onSeeTransactionsClick(tokenType: TokenType): void {
        let chain = this.web3Config.targetChain;
        switch (tokenType) {
            case TokenType.MON:
                chain = ethereum;
                break;
            default:
                break;
        }
        Redirector.openTransactionDetails(chain, this.web3Model.smartWalletAddress);
    }

    private onWeb3ModelPropertyChanged(model: Web3Model, key: string): void {
        switch (key) {
            case nameof(model, "erc20BalancesOnBase"):
                const usdcPanel = this.tokenPanels.get(TokenType.USDC);
                if (usdcPanel == null) {
                    this.logger.debug(`Unable to update balance of USDC panel, not defined`);
                    break;
                }
                usdcPanel.balance = this.formatBalance(model.erc20BalancesOnBase.get(this.web3Config.reservoirDevUsdcCurrency));
                break;
            case nameof(model, "erc20BalancesOnL1"):
                const monPanel = this.tokenPanels.get(TokenType.MON);
                if (monPanel == null) {
                    this.logger.debug(`Unable to update balance of MON panel, not defined`);
                    break;
                }
                monPanel.balance = this.formatBalance(model.erc20BalancesOnL1.get(this.web3Config.monCurrencyAddress));
                break;
        }
    }
}
